import React from "react";
import ReactDOM from "react-dom";
import { compose } from "redux";

import AppStateHOC from "../lib/app-state-hoc.jsx";
import GUI from "../containers/gui.jsx";
import HashParserHOC from "../lib/hash-parser-hoc.jsx";
import log from "../lib/log.js";
// history.length when this tab first loaded the CURRENT visit to the editor
// (i.e. the entry we came from the code page into). Kept in sessionStorage so
// an in-place reload (e.g. a device/version URL switch) doesn't reset it —
// but sessionStorage survives the whole tab session, not just one visit, so
// it must be recaptured on every genuinely NEW navigation into the editor
// (clicking "Block Based" again from plode-web after having left and come
// back); otherwise a second visit reuses the first visit's stale reference
// point and the back button overshoots to whatever far-earlier page happens
// to sit at that history depth (e.g. landing on /kitSelection instead of
// /code).
const ENTRY_KEY = 'blockbasedEntryHistoryLength';
try {
    const navEntry = performance.getEntriesByType('navigation')[0];
    const isFreshNavigation = !navEntry || navEntry.type === 'navigate';
    if (isFreshNavigation || sessionStorage.getItem(ENTRY_KEY) === null) {
        sessionStorage.setItem(ENTRY_KEY, String(history.length));
    }
    if (isFreshNavigation) {
        sessionStorage.removeItem('sensorVariant');
    }
    const launchCookie = document.cookie.match(/(?:^|; )peeCeeLaunchVariant=([^;]+)/);
    if (launchCookie) {
        document.cookie = 'peeCeeLaunchVariant=; path=/; max-age=0';
        if ((launchCookie[1] === '1' || launchCookie[1] === '2') &&
            sessionStorage.getItem('sensorVariant') === null) {
            sessionStorage.setItem('sensorVariant', launchCookie[1]);
        }
    }
} catch (e) {
    // sessionStorage/Navigation Timing unavailable — fall back to the old fixed jump below
}

const onClickLogo = () => {
    // Go back to the code page no matter how many entries (version switches,
    // modals, new-project pushes) were added since the editor was first opened.
    let steps = 2;
    try {
        const entryLength = parseInt(sessionStorage.getItem(ENTRY_KEY), 10);
        if (!isNaN(entryLength)) {
            steps = Math.max(history.length - entryLength + 1, 1);
        }
    } catch (e) {
        // keep fallback
    }
    history.go(-steps);
    //window.location = "https://plode.org/visualProgram";
};

const handleTelemetryModalCancel = () => {
    log("User canceled telemetry modal");
};

const handleTelemetryModalOptIn = () => {
    log("User opted into telemetry");
};

const handleTelemetryModalOptOut = () => {
    log("User opted out of telemetry");
};

/*
 * Render the GUI playground. This is a separate function because importing anything
 * that instantiates the VM causes unsupported browsers to crash
 * {object} appTarget - the DOM element to render to
 */
export default (appTarget) => {
    GUI.setAppElement(appTarget);

    // note that redux's 'compose' function is just being used as a general utility to make
    // the hierarchy of HOC constructor calls clearer here; it has nothing to do with redux's
    // ability to compose reducers.
    const WrappedGui = compose(AppStateHOC, HashParserHOC)(GUI);

    // TODO a hack for testing the backpack, allow backpack host to be set by url param
    const backpackHostMatches = window.location.href.match(
        /[?&]backpack_host=([^&]*)&?/
    );
    const backpackHost = backpackHostMatches ? backpackHostMatches[1] : null;

    const scratchDesktopMatches = window.location.href.match(
        /[?&]isScratchDesktop=([^&]+)/
    );
    let simulateScratchDesktop;
    if (scratchDesktopMatches) {
        try {
            // parse 'true' into `true`, 'false' into `false`, etc.
            simulateScratchDesktop = JSON.parse(scratchDesktopMatches[1]);
        } catch {
            // it's not JSON so just use the string
            // note that a typo like "falsy" will be treated as true
            simulateScratchDesktop = scratchDesktopMatches[1];
        }
    }

    if (process.env.NODE_ENV === "production" && typeof window === "object") {
        // Warn before navigating away
        window.onbeforeunload = () => true;
    }

    ReactDOM.render(
        // important: this is checking whether `simulateScratchDesktop` is truthy, not just defined!
        simulateScratchDesktop ? (
            <WrappedGui
                canEditTitle
                isScratchDesktop
                showTelemetryModal
                canSave={false}
                onTelemetryModalCancel={handleTelemetryModalCancel}
                onTelemetryModalOptIn={handleTelemetryModalOptIn}
                onTelemetryModalOptOut={handleTelemetryModalOptOut}
            />
        ) : (
            <WrappedGui
                canEditTitle
                backpackVisible
                showComingSoon
                backpackHost={backpackHost}
                canSave={false}
                onClickLogo={onClickLogo}
            />
        ),
        appTarget
    );
};
